import React, { useEffect, useState } from 'react';
import { Grid, CircularProgress } from '@mui/material';
import { useNavigate, Navigate } from 'react-router-dom';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { fetchXopsIntelligenceDashboards } from 'service/service';
import { useTheme } from '@mui/material/styles';
import '../LandingPage/LandingPage.scss';
import { ReactComponent as XOPSSidebarOverviewLightIcon } from '../../assets/PepWise2.0/XOPS_Sidebar_overview_Light.svg';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface XopsDashboard {
	_id: string;
	dashboardName: string;
	slug: string;
	priority: number;
	status: string;
	url: string;
	// 'internal' = a native Portal route (rendered in-app, no iframe).
	urlType: 'iframe' | 'external' | 'redirect' | 'internal';
}

const XOpsIntelligence: React.FC = () => {
	const theme = useTheme();
	const colors = theme.palette;
	const [controller] = usePepwiseAuthController();
	const { capabilities } = controller;
	const navigate = useNavigate();

	const [dashboards, setDashboards] = useState<XopsDashboard[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const isEntitled = (capabilities ?? []).includes('Data Ops');

	useEffect(() => {
		if (!isEntitled) return;

		const loadDashboards = async () => {
			try {
				setLoading(true);
				const response = await fetchXopsIntelligenceDashboards();
				const rawData = response?.data || [];
				// Filter to only include dashboards with Active status
				const data = rawData.filter((item: XopsDashboard) => item.status?.toLowerCase() === 'active');
				// Sort by priority ascending, then alphabetically by dashboardName for ties
				const sorted = [...data].sort((a: XopsDashboard, b: XopsDashboard) => {
					if (a.priority !== b.priority) return a.priority - b.priority;
					return a.dashboardName.localeCompare(b.dashboardName);
				});
				setDashboards(sorted);
				setError(null);
			} catch (err) {
				console.error('Failed to load XOps Intelligence dashboards:', err);
				setError('Failed to load dashboards. Please try again later.');
			} finally {
				setLoading(false);
			}
		};

		loadDashboards();
	}, [isEntitled]);

	if (!isEntitled) {
		return <Navigate to="/LandingPage" replace />;
	}

	const handleCardClick = (dashboard: XopsDashboard) => {
		if (dashboard.urlType === 'redirect') {
			window.open(dashboard.url, '_blank', 'noopener,noreferrer');
		} else if (dashboard.urlType === 'internal') {
			navigate(dashboard.url);
		} else {
			navigate(`/dashboard/xops-intelligence/${dashboard.slug}`, {
				state: { dashboard }
			});
		}
	};

	const iconProps = { width: 50, height: 50, style: { margin: '0 auto' } };

	if (loading) {
		return (
			<div className="outerLandingPageContainer" style={{ backgroundColor: `${colors.backgroundLight.primary}` }}>
				<div style={{ maxWidth: '86%', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
					<CircularProgress size={40} />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="outerLandingPageContainer" style={{ backgroundColor: `${colors.backgroundLight.primary}` }}>
				<div style={{ maxWidth: '86%', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
					<p style={{ color: '#d32f2f', fontFamily: 'Montserrat, sans-serif', fontSize: '16px' }}>{error}</p>
				</div>
			</div>
		);
	}
	return (
		<div className="outerLandingPageContainer" style={{ backgroundColor: `${colors.backgroundLight.primary}`, paddingTop: '70px', minHeight: 'calc(100vh - 70px)' }}>
			<div style={{ maxWidth: '92%', margin: '0 auto' }}>
				<Grid container spacing={3}>
					{dashboards.map((dashboard) => (
						<Grid
							item
							xs={3}
							key={dashboard._id || dashboard.slug}
							style={{ cursor: 'pointer' }}
							onClick={() => handleCardClick(dashboard)}
							onContextMenu={(e) => {
								e.preventDefault();
							}}
						>
							<div className="observalbility_screens" style={{ textAlign: 'center', position: 'relative', minHeight: '115px' }}>
								{/* Redirect indicator icon — top-right corner */}
								{dashboard.urlType === 'redirect' && (
									<OpenInNewIcon
										style={{
											position: 'absolute',
											top: 10,
											right: 10,
											fontSize: 18,
											color: '#e5e5e5',
										}}
									/>
								)}
								<div className="title">
									<XOPSSidebarOverviewLightIcon {...iconProps} />
								</div>
								<div>
									<h3 className="obsTitle" style={{ margin: '10px 0' }}>
										{dashboard.dashboardName}
									</h3>
								</div>
							</div>
						</Grid>
					))}
				</Grid>

				{dashboards.length === 0 && !loading && (
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
						<p style={{ color: `${colors.textLight.secondary}`, fontFamily: 'Montserrat, sans-serif', fontSize: '16px' }}>
							No dashboards available.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default XOpsIntelligence;
